/*
 * [KP] Lead forms → n8n.
 * One module for every enquiry form on the site (contact page, product page,
 * brand landing pages, quote pages). A form opts in with a wrapper:
 *
 *   <div data-kp-lead data-kp-webhook="…" data-kp-service="exhaust" …>
 *     {% form 'contact' %} … inputs named contact[key] … {% endform %}
 *   </div>
 *
 * The payload keeps the shape of the original /pages/formularz forms
 * (service, mode, shadowValue, referenceNumber, fields, structured,
 * fieldMetadata, photos), so the existing n8n workflow reads it as before.
 * Inputs keep Shopify's contact[...] names, so without JavaScript the form
 * still posts to the Shopify inbox.
 *
 * What it adds over the old forms: required fields are checked before
 * sending, a failed request shows an error with WhatsApp/phone instead of a
 * false "sent", and a successful one fires generate_lead (dataLayer, gtag)
 * plus the Shopify customer event kp_lead_submitted.
 */
(() => {
  if (window.KPLead) return;

  const SECTIONS = {
    vehicle: ['make', 'model', 'year', 'engine', 'vin', 'registration', 'mileage'],
    details: ['service', 'scope', 'description', 'expectations', 'budget', 'timeline', 'topic', 'notes', 'product_title', 'product_url', 'product_sku', 'variant'],
    contact: ['name', 'email', 'phone', 'company', 'location', 'contact_preference'],
  };
  const FULL_QUOTES = ['exhaust', 'chip', 'mechanical', 'visual'];

  const keyOf = (name) => {
    const match = /^contact\[(.+)\]$/.exec(name || '');
    return match ? match[1] : null;
  };

  const sectionOf = (key) => {
    if (key.indexOf('consent') === 0) return 'consents';
    for (const section in SECTIONS) {
      if (SECTIONS[section].indexOf(key) !== -1) return section;
    }
    return 'other';
  };

  const labelOf = (el) => {
    if (el.dataset.label) return el.dataset.label;
    const label = el.labels && el.labels[0];
    if (!label) return keyOf(el.name) || el.name;
    return label.textContent.replace(/\*/g, '').replace(/\(opcjonalnie\)/gi, '').replace(/\s+/g, ' ').trim();
  };

  const refNumber = (service, mode) => {
    const prefix = (service || 'REQ').substring(0, 3).toUpperCase();
    let letter = 'F';
    if (mode === 'quick') letter = 'Q';
    else if (service === 'service') letter = 'S';
    else if (service === 'concierge') letter = 'C';
    const d = new Date();
    const stamp = String(d.getFullYear()).slice(-2) + String(d.getMonth() + 1).padStart(2, '0') + String(d.getDate()).padStart(2, '0');
    return `${prefix}${letter}-${stamp}-${Math.random().toString(36).substring(2, 6).toUpperCase()}`;
  };

  const shadowValue = (service, mode) => {
    if (mode === 'quick') return 'quick_contact';
    if (mode === 'service') return 'service_booking';
    if (mode === 'concierge') return 'concierge_consultation';
    return FULL_QUOTES.indexOf(service) !== -1 ? `${service}_full_quote` : 'full_quote';
  };

  const wrapOf = (form) => form.closest('[data-kp-lead]');

  /* ---------- fields shown for one service only ---------- */
  const syncServiceFields = (wrap) => {
    const select = wrap.querySelector('select[name="contact[service]"]');
    const current = select ? select.value : wrap.dataset.kpService || '';
    wrap.querySelectorAll('[data-kp-show-for]').forEach((group) => {
      const list = group.dataset.kpShowFor.split(',').map((s) => s.trim());
      const show = list.indexOf(current) !== -1;
      group.hidden = !show;
      group.querySelectorAll('input, select, textarea').forEach((el) => { el.disabled = !show; });
    });
  };

  /* ---------- validation ---------- */
  const validate = (form) => {
    const wrap = wrapOf(form);
    const phone = form.querySelector('[name="contact[phone]"]');
    const email = form.querySelector('[name="contact[email]"]');

    if (phone) {
      const digits = phone.value.replace(/\D/g, '');
      phone.setCustomValidity(phone.value.trim() && digits.length < 9 ? (wrap.dataset.kpPhoneMessage || 'Podaj pełny numer telefonu (min. 9 cyfr).') : '');
    }
    if (wrap.dataset.kpEither !== undefined && phone && email && !phone.validationMessage) {
      const none = !phone.value.trim() && !email.value.trim();
      phone.setCustomValidity(none ? (wrap.dataset.kpEitherMessage || 'Podaj telefon albo e-mail.') : '');
    }
    form.querySelectorAll('[data-kp-min]').forEach((el) => {
      const min = parseInt(el.dataset.kpMin, 10) || 0;
      const length = el.value.replace(/\s+/g, ' ').trim().length;
      el.setCustomValidity(length && length < min ? (el.dataset.kpMinMessage || '') : '');
    });
    // Required fields that hold only spaces count as empty.
    form.querySelectorAll('[required]').forEach((el) => {
      if (el.type !== 'checkbox' && el.tagName !== 'SELECT' && !el.value.trim()) el.value = '';
    });

    if (form.checkValidity()) return true;
    const invalid = form.querySelector(':invalid');
    if (invalid) {
      invalid.reportValidity();
      invalid.focus({ preventScroll: false });
    }
    return false;
  };

  /* ---------- payload ---------- */
  const payloadFor = (form) => {
    const wrap = wrapOf(form);
    const data = wrap.dataset;
    const fields = {};
    const fieldMetadata = {};

    Array.prototype.forEach.call(form.elements, (el) => {
      const key = keyOf(el.name);
      if (!key || el.disabled || el.type === 'file') return;
      if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
      let value = el.type === 'checkbox' ? (el.value && el.value !== 'on' ? el.value : true) : String(el.value).trim();
      if (value === '') return;
      // Lists with code|name options: the e-mail shows the name, n8n gets the code as "service".
      if (el.tagName === 'SELECT' && el.selectedIndex > -1) {
        const text = el.options[el.selectedIndex].text.trim();
        if (text && text !== value) value = text;
      }
      if (key.indexOf('consent') === 0) value = true;
      fields[key] = fields[key] === undefined ? value : [].concat(fields[key], value);
      if (!fieldMetadata[key]) {
        fieldMetadata[key] = {
          label: labelOf(el),
          type: el.tagName === 'TEXTAREA' ? 'textarea' : el.tagName === 'SELECT' ? 'select' : el.type,
          section: el.dataset.section || sectionOf(key),
          required: el.required,
          step: data.kpStep || 'form',
        };
      }
    });

    const select = form.querySelector('select[name="contact[service]"]');
    const service = (select && select.value) || data.kpService || 'contact';
    const serviceName = (select && select.selectedIndex > 0 && select.options[select.selectedIndex].text) || data.kpServiceName || '';
    // Service bookings and Concierge used their own modes in the old forms.
    let mode = data.kpMode || 'quick';
    if (mode === 'full' && (service === 'service' || service === 'concierge')) mode = service;

    // Same section order in every e-mail: vehicle, details, contact, consents.
    const structured = {};
    ['vehicle', 'details', 'contact', 'consents', 'other'].forEach((section) => {
      Object.keys(fields).forEach((key) => {
        const meta = fieldMetadata[key];
        if (meta.section !== section) return;
        if (!structured[section]) structured[section] = {};
        structured[section][key] = { value: fields[key], label: meta.label, type: meta.type };
      });
    });

    return {
      service,
      serviceName,
      serviceType: data.kpServiceType || '',
      mode,
      shadowValue: shadowValue(service, mode),
      referenceNumber: refNumber(service, mode),
      submittedAt: new Date().toISOString(),
      pageUrl: window.location.href,
      source: data.kpSource || '',
      fields,
      structured,
      fieldMetadata,
      photos: (wrap._kpPhotos || []).slice(),
    };
  };

  /* ---------- photos (Cloudinary, unsigned preset as before) ---------- */
  const initUpload = (wrap) => {
    const input = wrap.querySelector('[data-kp-upload]');
    if (!input || !wrap.dataset.kpCloud || !wrap.dataset.kpPreset) return;
    const status = wrap.querySelector('[data-kp-upload-status]');
    const max = parseInt(input.dataset.kpUploadMax || '6', 10);
    wrap._kpPhotos = [];
    wrap._kpUploads = [];

    const setStatus = () => {
      if (!status) return;
      const pending = wrap._kpUploads.filter((u) => !u.done).length;
      const count = wrap._kpPhotos.length;
      status.textContent = pending
        ? (input.dataset.kpUploading || 'Wysyłanie zdjęć…') + ` (${count}/${count + pending})`
        : count ? (input.dataset.kpUploaded || 'Dodane zdjęcia:') + ` ${count}` : '';
    };

    input.addEventListener('change', () => {
      const files = Array.prototype.slice.call(input.files || []).slice(0, Math.max(0, max - wrap._kpPhotos.length));
      files.forEach((file) => {
        if (!/^image\//.test(file.type) || file.size > 10 * 1024 * 1024) return;
        const body = new FormData();
        body.append('file', file);
        body.append('upload_preset', wrap.dataset.kpPreset);
        const job = { done: false };
        job.promise = fetch(`https://api.cloudinary.com/v1_1/${wrap.dataset.kpCloud}/image/upload`, { method: 'POST', body })
          .then((res) => (res.ok ? res.json() : null))
          .then((json) => {
            if (json && json.secure_url) {
              wrap._kpPhotos.push({ id: json.public_id, url: json.secure_url, thumbnail: json.secure_url.replace('/upload/', '/upload/c_thumb,w_200,h_200/') });
            }
          })
          .catch(() => {})
          .finally(() => { job.done = true; setStatus(); });
        wrap._kpUploads.push(job);
      });
      input.value = '';
      setStatus();
    });
  };

  /* ---------- result states ---------- */
  const fill = (text, values) => Object.keys(values).reduce((out, key) => out.split(`[${key}]`).join(values[key]), text || '');

  const waLink = (wrap, text) => {
    const digits = (wrap.dataset.kpWa || '').replace(/\D/g, '');
    return digits ? `https://wa.me/${digits}?text=${encodeURIComponent(text)}` : '';
  };

  const showSuccess = (form, payload) => {
    const wrap = wrapOf(form);
    const box = wrap.querySelector('[data-kp-success]');
    if (!box) return;
    const ref = box.querySelector('[data-kp-ref]');
    if (ref) ref.textContent = payload.referenceNumber;
    const wa = box.querySelector('[data-kp-success-wa]');
    if (wa) {
      const link = waLink(wrap, fill(wrap.dataset.kpWaSuccess || 'Dzień dobry, wysłałem zgłoszenie [numer].', { numer: payload.referenceNumber }));
      if (link) wa.href = link; else wa.hidden = true;
    }
    form.hidden = true;
    wrap.classList.add('is-sent');
    box.hidden = false;
    box.focus({ preventScroll: true });
    box.scrollIntoView({ behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
  };

  const showError = (form, payload) => {
    const wrap = wrapOf(form);
    const box = wrap.querySelector('[data-kp-error]');
    if (!box) return;
    const f = payload.fields;
    const summary = [f.service ? payload.serviceName || f.service : payload.serviceName, [f.make, f.model, f.year].filter(Boolean).join(' '), f.product_title, f.description].filter(Boolean).join(' – ');
    const wa = box.querySelector('[data-kp-error-wa]');
    if (wa) {
      const link = waLink(wrap, fill(wrap.dataset.kpWaError || 'Dzień dobry, formularz się nie wysłał. Moja sprawa: [sprawa]', { sprawa: summary || '' }));
      if (link) wa.href = link; else wa.hidden = true;
    }
    box.hidden = false;
    box.focus({ preventScroll: false });
  };

  const track = (payload) => {
    const detail = { form_service: payload.service, form_mode: payload.mode, form_source: payload.source };
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: 'generate_lead' }, detail)); } catch (e) {}
    try { if (typeof window.gtag === 'function') window.gtag('event', 'generate_lead', detail); } catch (e) {}
    try { if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') Shopify.analytics.publish('kp_lead_submitted', detail); } catch (e) {}
  };

  /* ---------- submit ---------- */
  const submit = async (form) => {
    const wrap = wrapOf(form);
    if (!wrap || wrap.dataset.kpBusy) return;
    const error = wrap.querySelector('[data-kp-error]');
    if (error) error.hidden = true;
    if (!validate(form)) return;

    const button = form.querySelector('[type="submit"]');
    wrap.dataset.kpBusy = '1';
    if (button) { button.disabled = true; button.setAttribute('aria-busy', 'true'); button.classList.add('loading'); }

    try {
      if (wrap._kpUploads && wrap._kpUploads.length) await Promise.all(wrap._kpUploads.map((u) => u.promise));
      const payload = payloadFor(form);
      const url = wrap.dataset.kpWebhook;
      let ok = false;
      try {
        const controller = 'AbortController' in window ? new AbortController() : null;
        const timer = controller ? setTimeout(() => controller.abort(), 20000) : null;
        const res = await fetch(url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          signal: controller ? controller.signal : undefined,
        });
        if (timer) clearTimeout(timer);
        ok = res.ok;
      } catch (e) {
        ok = false;
      }
      if (ok) {
        track(payload);
        showSuccess(form, payload);
      } else {
        showError(form, payload);
      }
    } finally {
      delete wrap.dataset.kpBusy;
      if (button) { button.disabled = false; button.removeAttribute('aria-busy'); button.classList.remove('loading'); }
    }
  };

  /* ---------- wiring ---------- */
  const init = (wrap) => {
    if (wrap.dataset.kpReady) return;
    wrap.dataset.kpReady = '1';
    const form = wrap.querySelector('form');
    if (!form) return;
    form.noValidate = true;

    const select = wrap.querySelector('select[name="contact[service]"]');
    if (select) {
      const wanted = new URLSearchParams(window.location.search).get('usluga');
      if (wanted && select.querySelector(`option[value="${CSS.escape(wanted)}"]`)) select.value = wanted;
      select.addEventListener('change', () => syncServiceFields(wrap));
    }
    syncServiceFields(wrap);
    initUpload(wrap);

    // Clear a custom message as soon as the field is edited again.
    form.addEventListener('input', (e) => { if (e.target.setCustomValidity) e.target.setCustomValidity(''); });

    // Forms with their own step logic call KPLead.submit themselves.
    if (wrap.dataset.kpManual !== undefined) return;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      submit(form);
    });
  };

  const scan = (root) => (root || document).querySelectorAll('[data-kp-lead]').forEach(init);

  window.KPLead = { submit, validate, scan };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => scan());
  else scan();
})();
